// client/src/components/react-public/global/Navbar.jsx

import React, {
  useState,
} from 'react';

import {
  Badge,
  Box,
  Button,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import {
  ShoppingCartOutlined,
} from '@mui/icons-material';

import {
  useNavigate,
} from 'react-router-dom';

import {
  useDispatch,
  useSelector,
} from 'react-redux';

import {
  setIsCartOpen,
} from '../../../features/cart/cartApiSlice';

import {
  selectCurrentToken,
} from '../../../features/auth/authSlice';

import LoginDialog from '../../../features/auth/LoginDialog';

import AccountMenu from '../../shared/AccountMenu';

import {
  navLinks,
} from '../../../constants';

import {
  usfuel,
  menu,
  close,
} from '../../../assets';


const Navbar = () => {
  const [
    toggle,
    setToggle,
  ] = useState(false);


  const [
    loginOpen,
    setLoginOpen,
  ] = useState(false);


  const [
    loginDestination,
    setLoginDestination,
  ] = useState(null);


  const navigate =
    useNavigate();

  const dispatch =
    useDispatch();

  const theme =
    useTheme();

  const isMobile =
    useMediaQuery(
      theme.breakpoints.down(
        'md'
      )
    );


  const cart =
    useSelector(
      (state) =>
        state.cart.cart
    );


  const token =
    useSelector(
      selectCurrentToken
    );


  const isLoggedIn =
    Boolean(
      token
    );


  /*
   * Normal login:
   *
   * authenticate, close modal,
   * stay on whatever public page
   * the user was viewing.
   */
  const handleLoginClick =
    () => {
      setLoginDestination(
        null
      );

      setLoginOpen(
        true
      );

      setToggle(
        false
      );
    };


  /*
   * ATG Console:
   *
   * logged in:
   *      /console immediately
   *
   * logged out:
   *      login modal
   *      then /console
   */
  const handleConsoleClick =
    () => {
      setToggle(
        false
      );

      if (
        isLoggedIn
      ) {
        navigate(
          '/console'
        );

        return;
      }

      setLoginDestination(
        '/console'
      );

      setLoginOpen(
        true
      );
    };


  const handleLoginClose =
    () => {
      setLoginOpen(
        false
      );

      setLoginDestination(
        null
      );
    };


  const handleLoginSuccess =
    ({
      isFirstLogin,
    } = {}) => {
      setLoginOpen(
        false
      );

      /*
       * Purchased/new account still needs
       * initial password reset.
       */
      if (
        isFirstLogin
      ) {
        setLoginDestination(
          null
        );

        navigate(
          '/reset-password'
        );

        return;
      }

      /*
       * User clicked ATG Console before
       * authenticating.
       */
      if (
        loginDestination
      ) {
        const destination =
          loginDestination;

        setLoginDestination(
          null
        );

        navigate(
          destination
        );

        return;
      }

      /*
       * Normal "Log In" button:
       *
       * stay on current public page.
       */
      setLoginDestination(
        null
      );
    };


  const handleNavClick =
    (id) => {
      setToggle(
        false
      );

      navigate(
        `/${id}`
      );
    };


  const cartButton = (
    <Badge
      badgeContent={
        cart.length
      }
      color="secondary"
    >
      <IconButton
        onClick={
          () =>
            dispatch(
              setIsCartOpen(
                true
              )
            )
        }

        sx={{
          color:
            'white',
        }}
      >
        <ShoppingCartOutlined />
      </IconButton>
    </Badge>
  );


  return (
    <>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        width="100%"
        py={
          2
        }
        px={
          3
        }
        bgcolor="background"
      >
        {/* LOGO */}

        <IconButton
          onClick={
            () =>
              navigate(
                '/'
              )
          }
        >
          <img
            src={
              usfuel
            }
            alt="USFuelPro Logo"
            className="w-[310px] h-[80px]"
          />
        </IconButton>


        {/* DESKTOP */}

        {!isMobile && (
          <Box
            display="flex"
            alignItems="center"
            gap={
              2
            }
          >
            {navLinks.map(
              (nav) => (
                <Box
                  key={
                    nav.id
                  }

                  sx={{
                    cursor:
                      'pointer',

                    display:
                      'flex',

                    alignItems:
                      'center',

                    gap:
                      1,
                  }}

                  onClick={
                    () =>
                      handleNavClick(
                        nav.id
                      )
                  }
                >
                  <nav.icon
                    style={{
                      color:
                        'white',
                    }}
                  />

                  <span
                    style={{
                      fontSize:
                        '0.9rem',

                      color:
                        'white',
                    }}
                  >
                    {nav.title}
                  </span>
                </Box>
              )
            )}


            {cartButton}


            {/* LOGGED OUT */}

            {!isLoggedIn && (
              <Button
                onClick={
                  handleLoginClick
                }

                sx={{
                  color:
                    'white',

                  fontWeight:
                    700,
                }}
              >
                Log In
              </Button>
            )}


            {/* CONSOLE */}

            <Button
              variant="outlined"
              onClick={
                handleConsoleClick
              }

              sx={{
                color:
                  'white',

                borderColor:
                  'rgba(255,255,255,0.6)',

                fontWeight:
                  700,

                '&:hover': {
                  borderColor:
                    'white',

                  backgroundColor:
                    'rgba(255,255,255,0.08)',
                },
              }}
            >
              ATG Console
            </Button>


            {/* LOGGED-IN ACCOUNT */}

            {isLoggedIn && (
              <AccountMenu />
            )}
          </Box>
        )}


        {/* MOBILE MENU BUTTON */}

        {isMobile && (
          <IconButton
            onClick={
              () =>
                setToggle(
                  (previous) =>
                    !previous
                )
            }
          >
            <img
              src={
                toggle
                  ? close
                  : menu
              }
              alt="Menu"
              className="w-[28px] h-[28px]"
            />
          </IconButton>
        )}
      </Box>


      {/* MOBILE DROPDOWN */}

      {isMobile && (
        <Box
          display={
            toggle
              ? 'flex'
              : 'none'
          }

          flexDirection="column"

          position="absolute"

          top={
            100
          }

          right={
            20
          }

          bgcolor="rgba(0, 0, 0, 0.95)"

          p={
            2
          }

          minWidth={
            240
          }

          borderRadius="20px"

          zIndex={
            1000
          }
        >
          {navLinks.map(
            (nav) => (
              <Box
                key={
                  nav.id
                }

                onClick={
                  () =>
                    handleNavClick(
                      nav.id
                    )
                }

                py={
                  1
                }

                px={
                  2
                }

                display="flex"

                alignItems="center"

                gap={
                  1
                }

                sx={{
                  cursor:
                    'pointer',

                  color:
                    'white',
                }}
              >
                <nav.icon />

                <span>
                  {nav.title}
                </span>
              </Box>
            )
          )}


          <Box
            sx={{
              display:
                'flex',

              alignItems:
                'center',

              px:
                1,

              py:
                1,
            }}
          >
            {cartButton}
          </Box>


          {!isLoggedIn && (
            <Button
              onClick={
                handleLoginClick
              }

              sx={{
                color:
                  'white',

                justifyContent:
                  'flex-start',

                px:
                  2,
              }}
            >
              Log In
            </Button>
          )}


          <Button
            onClick={
              handleConsoleClick
            }

            sx={{
              color:
                'white',

              justifyContent:
                'flex-start',

              px:
                2,
            }}
          >
            ATG Console
          </Button>


          {isLoggedIn && (
            <Box
              sx={{
                px:
                  1,

                pt:
                  1,

                display:
                  'flex',

                alignItems:
                  'center',

                gap:
                  1,
              }}
            >
              <AccountMenu />

              <Box
                sx={{
                  color:
                    'white',

                  fontWeight:
                    700,
                }}
              >
                Account
              </Box>
            </Box>
          )}
        </Box>
      )}


      {/* LOGIN MODAL */}

      <LoginDialog
        open={
          loginOpen
        }

        onClose={
          handleLoginClose
        }

        onSuccess={
          handleLoginSuccess
        }
      />
    </>
  );
};


export default Navbar;